import React, { useEffect, useState } from "react";
import type { AnyRoute } from "@tanstack/react-router";
import {
  Link,
  createFileRoute,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { isClerkAPIResponseError } from "~/lib/clerk";
import type { UserSigninForm } from "~/lib/schemas/user-auth";
import { UserSigninSchema } from "~/lib/schemas/user-auth";
import ImageLogoHorizontal from "~/img/logo/dcm-horizontal-1.png";
import { createNoIndexMeta } from "~/lib/shared/meta";

import { Card } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Button } from "~/components/ui/button";
import { FormField } from "~/components/ui/form-field";

import { useClerk } from "@clerk/tanstack-react-start";
import { WaveBackground } from "~/components/ui/wave-background";

export const Route = createFileRoute("/_auth/signin/")({
  validateSearch: (search: { [key: string]: unknown }) =>
    ({
      from: (search.from as string) || undefined,
      redirect: (search.redirect as string) || undefined,
    }) as { from?: string; redirect?: string },
  head: () => {
    const metaConfig = createNoIndexMeta(
      "Sign In – Dream Coach Match",
      "Sign in to your Dream Coach Match account to access your coaching dashboard and manage your profile.",
    );

    return {
      ...metaConfig,
      links: [{ rel: "canonical", href: "https://dreamcoachmatch.com/signin" }],
    };
  },
  component: SigninPage,
});

function SigninPage() {
  const { register, handleSubmit, formState } = useForm<UserSigninForm>({
    resolver: zodResolver(UserSigninSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });
  const { errors } = formState;

  const navigate = useNavigate();
  const clerk = useClerk();
  const search = useSearch({ from: "/_auth/signin/" });
  const fromAuthFailure = "auth-failure" === search?.from;
  const redirectTo = search?.redirect || "/settings";

  // Check if user had a valid session that expired vs never logged in
  const hadValidSession = fromAuthFailure && clerk?.isSignedIn;

  const [isProcessing, setIsProcessing] = useState(false);
  const [state, setState] = useState({
    code: ["", "", "", "", "", ""],
    error: "",
    resendTimer: 60,
    canResend: false,
  });
  const [authError, setAuthError] = useState<string | null>(null);
  const [redirectAttempts, setRedirectAttempts] = useState(0);

  useEffect(() => {
    if (clerk?.isSignedIn && 3 > redirectAttempts && !fromAuthFailure) {
      setRedirectAttempts((prev) => prev + 1);

      // Delay navigation to allow state to settle
      const timer = setTimeout(() => {
        navigate({ to: redirectTo as AnyRoute["fullPath"] }).catch((_error) => {
          setAuthError("Navigation failed. Please try refreshing the page.");
        });
      }, 100);
      return () => clearTimeout(timer);
    }
    if (redirectAttempts >= 3) {
      setAuthError(
        "Authentication error: Unable to access your account. Please clear your browser cache and try again.",
      );
    }
  }, [clerk?.isSignedIn, navigate, redirectAttempts, fromAuthFailure]);

  // OTP handlers
  const startResendTimer = () => {
    setState((prev) => ({
      ...prev,
      resendTimer: 60,
      canResend: false,
    }));

    const timer = setInterval(() => {
      setState((prev) => {
        const newTimer = prev.resendTimer - 1;
        if (0 >= newTimer) {
          clearInterval(timer);
          return { ...prev, resendTimer: 0, canResend: true };
        }
        return { ...prev, resendTimer: newTimer };
      });
    }, 1000);
  };

  const [_currentStepIndex, setCurrentStepIndex] = useState(0);

  const onSubmit = async (data: UserSigninForm) => {
    // Check if already signed in before attempting sign in
    if (clerk?.isSignedIn) {
      navigate({ to: "/settings" });
      return;
    }

    startResendTimer();
    setState((prev) => ({ ...prev, error: "" }));
    setIsProcessing(true);

    try {
      if (!clerk?.client) {
        setState((prev) => ({
          ...prev,
          error: "Authentication service not available",
        }));
        setIsProcessing(false);
        return;
      }

      const { email, password } = data;

      if (!email) {
        setState((prev) => ({ ...prev, error: "Email is required." }));
        setIsProcessing(false);
        return;
      }

      const signInAttempt = await clerk.client.signIn.create({
        identifier: email,
        password,
      });

      if ("complete" === signInAttempt.status) {
        await clerk.setActive({
          session: signInAttempt.createdSessionId,
        });

        // Small delay to ensure session is set before navigation
        setTimeout(() => {
          // Navigate to redirect target or default settings
          navigate({ to: redirectTo as AnyRoute["fullPath"] });
        }, 100);
      } else {
        // Handle incomplete sign in (MFA, etc.)
        setCurrentStepIndex(1);
      }

      setIsProcessing(false);
    } catch (err) {
      if (isClerkAPIResponseError(err)) {
        setState((prev) => ({
          ...prev,
          error:
            err.errors[0]?.longMessage || "Failed to create sign in attempt",
        }));
      } else {
        setState((prev) => ({
          ...prev,
          error: "An unexpected error occurred. Please try again.",
        }));
      }
      setIsProcessing(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center gap-6">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Card className="sm:w-145 w-100 flex flex-col p-8 shadow-lg hover:shadow-xl z-10 relative">
          <img
            src={ImageLogoHorizontal}
            alt="Dream Coach Match"
            className="w-[203px] z-10 -ml-2"
          />
          <div className="w-full flex-shrink-0 space-y-4">
            <h2 className="text-xl font-semibold">Log in</h2>

            {fromAuthFailure && (
              <Card className="mb-4 p-3 bg-blue-50 border-blue-200">
                <p className="text-sm text-blue-800 font-medium">
                  {hadValidSession ? "Session Expired" : "Sign In Required"}
                </p>
                <p className="text-sm text-blue-700 mt-1">
                  {hadValidSession
                    ? "Your session has expired. Please sign in again to continue"
                    : "Please sign in to access this page"}
                  {"/settings" !== redirectTo && ` (${redirectTo})`}.
                </p>
              </Card>
            )}

            {authError && (
              <Card error className="mb-4 p-3 bg-red-50">
                <p className="text-sm text-red-700 font-medium">
                  Authentication Error
                </p>
                <p className="text-sm text-red-600 mt-1">{authError}</p>
                <Button
                  variant="destructive-ghost"
                  size="sm"
                  onClick={() => window.location.reload()}
                  className="mt-2 h-auto p-1 text-xs"
                >
                  Refresh page
                </Button>
              </Card>
            )}

            <div className="space-y-2">
              <FormField
                fullWidth
                label="Enter email or phone number"
                error={errors.email?.message || state.error}
              >
                <Input
                  {...register("email")}
                  type="email"
                  placeholder="Enter email or phone number"
                  error={!!errors.email}
                />
              </FormField>
            </div>

            <div>
              <div className="space-y-2">
                <FormField
                  fullWidth
                  label="Password"
                  error={errors.password?.message}
                >
                  <Input
                    {...register("password")}
                    type="password"
                    placeholder="Password"
                    error={!!errors.password}
                  />
                </FormField>
              </div>
              <div className="font-400 mt-4 text-right text-sm">
                <Link
                  to="/forgot-password"
                  className="text-text-primary/100 font-600 hover:underline"
                >
                  Forgot?
                </Link>
              </div>
            </div>

            <Button
              type="submit"
              loading={isProcessing}
              className="mb-2 w-full"
            >
              Log in
            </Button>
            <div id="clerk-captcha" className="w-full" />
          </div>

          <div className="font-400 mt-4 text-center text-sm">
            Don't have an account?{" "}
            <Link
              to="/signup"
              className="text-text-primary/100 font-600 hover:underline"
            >
              Sign up
            </Link>
          </div>
        </Card>
      </form>
      <WaveBackground />
    </div>
  );
}
